<template>
  <section class="holiday-table">
    <div>
      <span>{{ $t('holiday_offdays') }}</span>
      <div>
        <Button :isClean="true" @click="openModal('add_offday')" skin="primary" :dense="true">{{ $t('add_offday') }}</Button>
        <Button :isClean="true" @click="openModal('add_holiday')" skin="primary" :dense="true">{{ $t('add_holiday') }}</Button>
      </div>
    </div>
    <Modal :parent="shiftParentType" width="medium" v-if="isAddingHoliday" @close="closeModal" :texts="{ headerPrimary: $t(headerTxt), headerSecondary: `/ ${companyName}` }" @submit="handleSubmit">
      <!-- Holiday mode: country/year selects -->
      <template v-if="headerTxt === 'add_holiday'">
        <section class="selects holiday">
          <div>
            <div>{{ $t('country') }}</div>
            <Select v-model="country" class="select" @input="fetchHolidays">
              <Option v-for="[code, country] in Object.entries(countries).filter((country, idx) => idx % 2 === 0)" :key="code" :value="code" :label="$t(country)" />
            </Select>
          </div>
          <div>
            <div>{{ $t('year') }}</div>
            <Select v-model="year" class="select" @input="fetchHolidays">
              <Option v-for="year in years" :key="year" :value="year" :label="year" />
            </Select>
          </div>
        </section>
      </template>
      <!-- Day-off mode: add / edit form -->
      <template v-else>
        <div class="offday-form-section">
          <div class="offday-form-title">{{ editingOffdayKey ? ($t('edit_offday') || 'Edit Day Off') : ($t('add_offday') || 'Add Day Off') }}</div>
          <section class="selects offday">
            <div>
              <div>{{ $t('offday_name') }}</div>
              <Input class="input" type="text" :value="formData.name" @input="formData.name = $event" />
              <ErrorMsg :isShown="formTouched.name && !isFieldValid('name', formData.name)" :text="`${$t('please_enter')} ${$t('name_under_chars')}`" />
            </div>
            <div>
              <div>{{ $t('year') }}</div>
              <Select class="select" :value="formData.year" @input="formData.year = $event">
                <Option v-for="year in years" :key="year" :value="year" :label="year" />
              </Select>
            </div>
            <div>
              <div>{{ $t('month') }}</div>
              <Select class="select" :value="formData.month" @input="formData.month = $event">
                <Option v-for="(month, code) in months" :key="code" :value="code" :label="$t(month)" />
              </Select>
            </div>
            <div>
              <div>{{ $t('day') }}</div>
              <Input class="input" type="text" :value="formData.day" @input="onDayInput($event)" />
              <ErrorMsg :isShown="formTouched.day && !isFieldValid('day', formData.day)" :text="`${$t('please_enter')} ${$t('days_comma_separated')}`" />
            </div>
          </section>
          <div class="form-actions">
            <Button v-if="editingOffdayKey" :isClean="true" @click="cancelEdit">{{ $t('cancel') }}</Button>
            <Button :isClean="true" :disabled="isFormError" skin="primary" @click="editingOffdayKey ? saveEdit() : addOffday()">{{ editingOffdayKey ? ($t('save') || 'Save') : ($t('add') || 'Add') }}</Button>
          </div>
        </div>
      </template>
      <!-- List header -->
      <header class="titles">
        <div v-for="title in [$t(offdayType === 'holidays' ? 'holiday_name' : 'offday_name'), $t('month'), $t('day'), $t('year')]" :key="title">
          {{ title }}
        </div>
      </header>
      <SearchBar class="holiday-search" v-model="listSearch" />
      <!-- List body -->
      <section class="holidays">
        <div v-if="!filteredHolidayItems.length" class="holiday-empty">{{ $t('no_results_found') }}</div>
        <template v-if="offdayType === 'holidays'">
          <article v-for="(item, idx) in filteredHolidays" :key="`holiday-${idx}`" class="holiday">
            <Checkbox :value="isChecked(item.name, item.start)" @input="handleCheck($event, `${item.name}-${item.start}`)" />
            <div class="name">{{ $t(item.name) }}</div>
            <div v-for="type in ['month', 'day', 'year']" :class="type" :key="type">{{ date(type, item.start) }}</div>
            <img v-show="!isShiftsOpen" :src="require('@/assets/edit-icon.svg')" title="Edit Shifts" alt="edit-shifts-icon" class="cell-icon action" @click="editShifts(offdayType, `${item.name}-${item.start}`, $event)" />
          </article>
        </template>
        <template v-else>
          <article v-for="(item, key) in filteredCustomOffdaysList" :key="key" class="holiday" :class="{ 'is-editing': editingOffdayKey === key }">
            <Checkbox :value="item.isChecked" @input="handleCheck($event, key)" />
            <div class="name offday">{{ $t(item.name) }}</div>
            <div v-for="type in ['month', 'day', 'year']" :class="type" :key="type">{{ date(type, item.start) }}</div>
            <img v-show="!isShiftsOpen && !editingOffdayKey && !confirmDeleteKey" :src="require('@/assets/edit-icon.svg')" title="Edit Day Off" alt="edit-icon" class="cell-icon action" @click="startEdit(key)" />
            <img v-show="!isShiftsOpen && !editingOffdayKey && !confirmDeleteKey" :src="require('@/assets/delete-icon.svg')" alt="remove-icon" class="cell-icon action" @click="confirmDeleteKey = key" />
            <img v-show="!isShiftsOpen" :src="require('@/assets/edit-icon.svg')" title="Edit Shifts" alt="edit-shifts-icon" class="cell-icon action" @click="editShifts(offdayType, key, $event)" />
          </article>
        </template>
        <template v-if="editedDay">
          <md-field class="multi-select offday">
            <md-select ref="multi-select" v-model="editedDay.shiftIds" name="shifts" id="shifts" multiple @md-closed="isShiftsOpen = false">
              <md-option v-for="{ id, shift_name } in shifts" :value="id" :key="id" :disabled="isDisabledShift(id)">{{ shift_name }}</md-option>
            </md-select>
          </md-field>
        </template>
      </section>
      <!-- Delete confirmation overlay -->
      <div v-if="confirmDeleteKey" class="delete-confirmation-overlay" @click.self="confirmDeleteKey = null">
        <div class="delete-confirmation-dialog">
          <div class="confirmation-icon-wrapper">
            <img :src="require('@/assets/warning_red.svg')" alt="warning" class="confirmation-icon" />
          </div>
          <div class="confirmation-content">
            <div class="confirmation-title">{{ $t('delete_confirmation_title') }}</div>
            <div class="confirmation-message">{{ $t('delete_confirmation_message') }}</div>
          </div>
          <div class="confirmation-buttons">
            <Button :isClean="true" @click="confirmDeleteKey = null" class="cancel-btn">{{ $t('cancel') }}</Button>
            <Button :isClean="true" @click="confirmDelete" skin="primary" class="confirm-btn">{{ $t('delete') }}</Button>
          </div>
        </div>
      </div>
    </Modal>
    <Table :tableData="selectedDays" class="selected-days-table" headerFontSize="11px" bodyFontSize="14px" />
  </section>
</template>

<script>
import Input from '@/modules/common/components/Input';
import Modal from '@/modules/common/components/Modal';
import Checkbox from '@/modules/common/components/Checkbox';
import Select from '@/modules/common/components/Select';
import Option from '@/modules/common/components/Option';
import Button from '@/modules/common/components/Button';
import ErrorMsg from '@/modules/common/components/ErrorMsg';
import Table from '@/modules/common/components/customTable/Table';
import SearchBar from '@/modules/common/components/SearchBar';
import siteService from '@/modules/control-panel/services/siteService';
import swalService from '@/modules/common/services/swalService';
import { months } from '../consts.js';

const VALIDATIONS = {
  day: /^(?:0?[1-9]|[12][0-9]|3[01])(?:,(?:0?[1-9]|[12][0-9]|3[01]))*$/,
  name: /^.{1,25}$/,
};

function emptyForm() {
  return { name: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: '' };
}

export default {
  props: {
    shiftParentType: {
      type: String,
      required: true,
      validator: (value) => ['site', 'shift'].includes(value),
    },
    siteId: { type: Number, required: true },
    companyId: { type: Number, required: true },
    companyName: { type: String, required: true },
    roomId: { type: Number, required: false },
    companyCountry: { type: String, required: false },
    shifts: { type: Object, required: false },
  },
  data() {
    return {
      holidays: [],
      offdays: { holidays: {}, custom: {} },
      formData: emptyForm(),
      formTouched: { name: false, day: false },
      editedDay: null,
      headerTxt: '',
      year: new Date().getFullYear(),
      country: null,
      isAddingHoliday: false,
      isShiftsOpen: false,
      shiftsAvailabilty: null,
      isNewlyAdded: false,
      confirmDeleteKey: null,
      editingOffdayKey: null,
      listSearch: '',
    };
  },
  async created() {
    await this.updateViewData();
  },
  methods: {
    // ─── Data loading ──────────────────────────────────
    async updateViewData() {
      await this.getSiteOffDays();
      this.country = this.countries[this.companyCountry.toLowerCase()];
      this.shiftsAvailabilty = { ...this.shifts };
      this.$emit('loaded');
    },
    async getSiteOffDays() {
      try {
        this.offdays = await siteService.getSiteOffDays(this.siteId);
      } catch (err) {
        swalService.errorMsg("Could not load site's holidays & days-off. Please reload to try again.");
      }
    },

    // ─── Modal open / close / submit ───────────────────
    async openModal(type) {
      this.headerTxt = type;
      this.listSearch = '';

      if (type === 'add_holiday') {
        const hasShifts = this.hasShifts();
        if (!hasShifts) {
          swalService.errorMsg(this.$t('please_add_shift_first') || 'Please add shifts first before adding holidays.');
          return;
        }
      }

      try {
        if (type === 'add_holiday') {
          if (!this.country) this.country = this.countries[this.companyCountry?.toLowerCase()];
          await this.fetchHolidays();
        }
        this.isAddingHoliday = true;
      } catch (err) {
        if (err.message !== 'No shifts available' && err.message !== 'Country not selected') {
          this.closeModal();
        }
      }
    },
    closeModal() {
      this.isAddingHoliday = false;
      this.listSearch = '';
      this.resetForm();
      this.getSiteOffDays();
    },
    async handleSubmit() {
      try {
        await siteService.updateSiteOffDays(this.siteId, this.offdays);
        swalService.savedMsg(this.$t('Your work has been saved'));
        this.closeModal();
      } catch (err) {
        swalService.errorMsg();
      }
    },

    // ─── Form helpers ──────────────────────────────────
    resetForm() {
      this.formData = emptyForm();
      this.formTouched = { name: false, day: false };
      this.editingOffdayKey = null;
      this.confirmDeleteKey = null;
    },
    isFieldValid(field, value) {
      if (!value && value !== 0) return false;
      return VALIDATIONS[field] ? VALIDATIONS[field].test(String(value)) : true;
    },
    onDayInput(value) {
      this.formData.day = value;
      this.formTouched.day = true;
    },
    hasShifts() {
      const keys = this.shifts && typeof this.shifts === 'object' ? Object.keys(this.shifts) : [];
      return keys.length > 0 && keys.some(key => this.shifts[key] && this.shifts[key].id);
    },

    // ─── ADD day off ───────────────────────────────────
    addOffday() {
      this.formTouched = { name: true, day: true };
      if (this.isFormError) return;

      const { year, month, name, day } = this.formData;
      const days = day.split(',');
      let added = false;

      days.forEach((d) => {
        const dateObj = new Date(year, month - 1, d);
        if (!(dateObj.getFullYear() == year && dateObj.getMonth() == month - 1 && dateObj.getDate() == d)) return;

        const start = `${year}-${month}-${d}`;
        if (Object.values(this.offdays.custom).find(({ start: s }) => s === start)) return;

        const key = `${name}-${start}`;
        const newOffday = { name, start, isChecked: true, lastChanged: new Date().toISOString() };
        this.$set(this.offdays.custom, key, newOffday);

        this.isNewlyAdded = true;
        this.editedDay = this.offdays.custom[key];
        this.getAvailableShifts();
        this.$set(this.offdays.custom[key], 'shiftIds', this.selectShifts('custom', key));
        this.isNewlyAdded = false;
        added = true;
      });

      if (added) {
        // Force Vue to see the new keys
        this.offdays = { ...this.offdays, custom: { ...this.offdays.custom } };
      }

      this.formData = emptyForm();
      this.formTouched = { name: false, day: false };
    },

    // ─── EDIT day off ──────────────────────────────────
    startEdit(key) {
      const offday = this.offdays.custom[key];
      if (!offday) return;

      const [year, month, day] = offday.start.split('-');
      this.editingOffdayKey = key;
      this.formData = { name: offday.name, year: parseInt(year), month: parseInt(month), day };
      this.formTouched = { name: true, day: true };
    },
    cancelEdit() {
      this.editingOffdayKey = null;
      this.formData = emptyForm();
      this.formTouched = { name: false, day: false };
    },
    saveEdit() {
      this.formTouched = { name: true, day: true };
      if (this.isFormError) return;

      const { year, month, name, day } = this.formData;
      const originalKey = this.editingOffdayKey;
      const days = day.split(',');

      // Validate all days
      for (const d of days) {
        const dateObj = new Date(year, month - 1, d);
        if (!(dateObj.getFullYear() == year && dateObj.getMonth() == month - 1 && dateObj.getDate() == d)) return;
      }

      const oldOffday = this.offdays.custom[originalKey];
      const newCustom = { ...this.offdays.custom };
      delete newCustom[originalKey];

      days.forEach((d) => {
        const start = `${year}-${month}-${d}`;
        const key = `${name}-${start}`;
        if (newCustom[key] && key !== originalKey) return;

        newCustom[key] = {
          name,
          start,
          isChecked: oldOffday ? (oldOffday.isChecked || true) : true,
          shiftIds: oldOffday ? (oldOffday.shiftIds || []) : [],
          lastChanged: new Date().toISOString(),
        };
      });

      this.$set(this.offdays, 'custom', newCustom);
      this.editingOffdayKey = null;
      this.formData = emptyForm();
      this.formTouched = { name: false, day: false };
    },

    // ─── DELETE day off ────────────────────────────────
    confirmDelete() {
      if (!this.confirmDeleteKey) return;

      const newCustom = { ...this.offdays.custom };
      delete newCustom[this.confirmDeleteKey];
      this.$set(this.offdays, 'custom', newCustom);
      this.confirmDeleteKey = null;
    },

    // ─── Holiday fetching ──────────────────────────────
    async fetchHolidays() {
      if (!this.hasShifts()) {
        swalService.errorMsg(this.$t('please_add_shift_first') || 'Please add shifts first before adding holidays.');
        throw new Error('No shifts available');
      }

      const { country, year } = this;
      if (!country) {
        swalService.errorMsg('Please select a country first.');
        throw new Error('Country not selected');
      }

      try {
        this.holidays = await siteService.getHolidayList(country, year);
        const isYearOffDaysEmpty = !Object.keys(this.offdays.holidays).some((key) => key.includes(year));
        if (country === 'il' && this.holidays.length && isYearOffDaysEmpty) {
          const defaultHolidays = this.defaultIsraelHolidays();
          this.holidays.forEach((holiday) => {
            if (defaultHolidays.includes(holiday.name)) this.handleCheck(true, `${holiday.name}-${holiday.start}`);
          });
        }
      } catch (err) {
        if (err.message !== 'No shifts available' && err.message !== 'Country not selected') {
          swalService.errorMsg('Could not load holidays list. Please try again later.');
        }
        throw err;
      }
    },
    defaultIsraelHolidays() {
      return [
        'Purim Eve', 'Purim', 'Passover Eve', 'Passover (Day 1)',
        'Yom HaZikaron', 'Yom HaAtzmaut', 'Rosh Hashana Eve', 'Rosh Hashana',
        'Rosh Hashana (Day 2)', 'Yom Kippur Eve', 'Yom Kippur', 'Sukkot Eve',
        'Sukkot (Day 1)', 'Sukkot (Day 2)', 'Sukkot (Day 3)', 'Sukkot (Day 4)',
        'Sukkot (Day 5)', 'Sukkot (Day 6)', 'Sukkot (Day 7) / Hoshanah Rabah',
        'Passover (Day 6)', 'Passover (Day 7)', 'Shavuot Eve', 'Shavuot',
      ];
    },

    // ─── Checkbox / shifts ─────────────────────────────
    handleCheck(value, holiday) {
      if (!this.shifts || Object.keys(this.shifts).length === 0) {
        swalService.errorMsg(this.$t('please_add_shift_first') || 'Please add shifts first before adding holidays.');
        return;
      }

      const { country } = this;
      const [name, year, month, day] = holiday.split('-');

      if (!this.offdays[this.offdayType][holiday]) {
        this.$set(this.offdays[this.offdayType], holiday, {
          name,
          country,
          start: `${year}-${month}-${day}`,
          lastChanged: new Date().toISOString(),
        });
        this.isNewlyAdded = true;
      }
      this.editedDay = this.offdays[this.offdayType][holiday];
      this.getAvailableShifts();
      this.$set(this.offdays[this.offdayType][holiday], 'shiftIds', this.selectShifts(this.offdayType, holiday));
      this.$set(this.offdays[this.offdayType][holiday], 'isChecked', value);
    },
    async editShifts(type, name, { x, y }) {
      if (!this.offdays[type][name] || (this.offdays[type][name] && !this.offdays[type][name].isChecked)) this.handleCheck(true, name);
      this.editedDay = this.offdays[type][name];

      await this.$nextTick();

      this.getAvailableShifts();
      if (!this.isNewlyAdded) this.editedDay.shiftIds = this.selectShifts(type, name);
      this.isShiftsOpen = true;

      const elContent = document.querySelector('.multi-select');
      elContent.style.top = `${y / 2 + 150}px`;
      elContent.style.left = `${x / 2}px`;

      await this.$nextTick();

      this.$refs['multi-select'].openSelect();
      this.isNewlyAdded = false;
    },
    getAvailableShifts() {
      if (!this.editedDay) return;
      if (!this.shifts || Object.keys(this.shifts).length === 0) return;
      if (!this.shiftsAvailabilty) return;
      const { start } = this.editedDay;
      const [year, month, day] = start.split('-');
      Object.values(this.shifts).forEach(({ id, week_arrivals }) => {
        if (this.shiftsAvailabilty[id]) {
          this.shiftsAvailabilty[id].disabled = !week_arrivals.includes(new Date(year, month - 1, day).getDay() + 1);
        }
      });
    },
    selectShifts(type, name) {
      if (!this.shiftsAvailabilty || Object.keys(this.shiftsAvailabilty).length === 0) return [];
      return Object.values(this.shiftsAvailabilty)
        .filter(({ id, disabled }) => (!this.isNewlyAdded && this.offdays?.[type]?.[name]?.shiftIds?.includes(id) && !disabled) || (this.isNewlyAdded && !disabled))
        .map(({ id }) => id);
    },
    matchesHolidaySearch(item) {
      if (!item) return false;
      const query = this.holidaySearchQuery;
      const name = String(this.$t(item.name) || item.name || '').toLowerCase();
      const start = String(item.start || '');
      return name.includes(query) || start.includes(query);
    },
  },
  computed: {
    date() {
      return (type, date) =>
        new Date(date).toLocaleDateString(JSON.parse(localStorage['language']), {
          [type]: (type === 'year') | (type === 'day') ? 'numeric' : 'long',
        });
    },
    months() {
      return months;
    },
    years() {
      return [new Date().getFullYear(), new Date().getFullYear() + 1];
    },
    countries() {
      return {
        il: 'israel', israel: 'il',
        us: 'USA', 'united states': 'us',
        pl: 'poland', poland: 'pl',
        mx: 'mexico', mexico: 'mx',
        cy: 'cyprus', cyprus: 'cy',
        br: 'brazil', brazil: 'br',
        au: 'australia', australia: 'au',
        ro: 'romania', romania: 'ro',
        nl: 'netherlands', netherlands: 'nl',
      };
    },
    isFormError() {
      const { name, day } = this.formData;
      return !this.isFieldValid('name', name) || !this.isFieldValid('day', day);
    },
    isChecked() {
      return (name, start) => this.offdays[this.offdayType]?.[`${name}-${start}`]?.isChecked;
    },
    customOffdaysList() {
      // Return a shallow copy so Vue tracks changes
      return { ...this.offdays.custom };
    },
    holidaySearchQuery() {
      return (this.listSearch || '').trim().toLowerCase();
    },
    filteredHolidays() {
      if (!this.holidaySearchQuery) return this.holidays;
      return this.holidays.filter((item) => this.matchesHolidaySearch(item));
    },
    filteredCustomOffdaysList() {
      if (!this.holidaySearchQuery) return this.customOffdaysList;
      return Object.entries(this.customOffdaysList).reduce((acc, [key, item]) => {
        if (this.matchesHolidaySearch(item)) acc[key] = item;
        return acc;
      }, {});
    },
    filteredHolidayItems() {
      return this.offdayType === 'holidays'
        ? this.filteredHolidays
        : Object.values(this.filteredCustomOffdaysList);
    },
    selectedDays() {
      const { offdays } = this;
      const selectedDays = Object.values({ ...offdays.custom, ...offdays.holidays }).filter(({ isChecked }) => isChecked);
      if (!selectedDays.length) return [{ name: 'placeholder', month: '', day: '', year: '' }];
      return selectedDays
        .slice()
        .sort((a, b) => {
          const [yearA, monthA, dayA] = String(a.start || '').split('-').map(Number);
          const [yearB, monthB, dayB] = String(b.start || '').split('-').map(Number);
          return (yearA - yearB) || (monthA - monthB) || (dayA - dayB);
        })
        .map(({ name, start }) => {
          const [year, month, day] = start.split('-');
          return { name, month: this.$t(months[month[0] < 1 ? month[1] : month]), day: day[0] < 1 ? day[1] : day, year };
        });
    },
    offdayType() {
      return this.headerTxt === 'add_holiday' ? 'holidays' : 'custom';
    },
    selectedShifts() {
      return (shifts) => shifts.reduce((acc, id) => acc + this.shifts[id].shift_name + ', ', '');
    },
    isDisabledShift() {
      return (id) => this.shiftsAvailabilty[id]?.disabled;
    },
  },
  watch: {
    siteId: {
      async handler() {
        await this.updateViewData();
      },
      immediate: true,
    },
    shifts: {
      handler() {
        this.shiftsAvailabilty = { ...this.shifts };
      },
      immediate: true,
      deep: true,
    },
    'formData.name'() {
      if (this.formData.name) this.formTouched.name = true;
    },
  },
  components: {
    Modal, Input, Checkbox, Select, Option, Button, ErrorMsg, Table, SearchBar,
  },
};
</script>

<style lang="scss">
.holiday-table,
.main:has(.shift-holiday) .main-content:not(:has(.form-container)) {
  .secondary-title {
    font-size: 24px !important;
  }

  .b-input {
    position: static !important;
    height: 0.85rem !important;
    width: 0.85rem !important;

    &::after {
      width: 0.2rem !important;
      height: 0.5rem !important;
      left: 0.37rem !important;
      top: 0.125rem !important;
    }
  }

  .main-content {
    margin-inline: 40px !important;
    gap: unset !important;
    margin-top: 40px !important;
  }

  .footer {
    justify-content: flex-end !important;
    margin-inline-end: 40px !important;
  }

  .close-btn {
    margin-inline-end: 40px !important;
  }

  .md-field {
    min-height: 46px !important;
    margin: 0 0 0 !important;

    .md-input {
      height: unset !important;
      max-height: 95% !important;
      width: 100px;
    }

    label {
      top: -28px;
    }

    &.md-has-value label,
    &.md-focused label {
      top: -28px;
      font-size: 16px;
    }
  }

  .error-msg {
    position: absolute;
    top: -33px;
    left: 0;
  }

  .table-row {
    grid-template-columns: 2fr 1fr 1fr 1fr !important;
    div:first-of-type {
      width: unset !important;
      max-width: 170px !important;
    }
    div:nth-of-type(n + 2) {
      width: unset !important;
      max-width: 70px !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;
$table-grey: #7f7f7f;

.holiday-table {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-inline: 50px;
  width: 100%;

  & > div:first-of-type {
    display: flex;
    justify-content: space-between;

    span {
      color: $table-grey;
      font-weight: bold;
    }

    div {
      display: flex;
      gap: 16px;
    }
  }

  & > div:nth-of-type(2) {
    padding-block: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    border: 1px solid #e7e7e7;
  }

  .add-button {
    cursor: pointer;
    color: $blue;
    line-height: unset;
  }
}

.holiday-table .main-content {
  gap: unset;
  position: relative;
}

/* ─── Offday form (shared for add & edit) ─── */
.offday-form-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-block-end: 20px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e7e7e7;

  .offday-form-title {
    font-size: 16px;
    font-weight: 600;
    color: $table-grey;
  }

  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }
}

.selects {
  display: flex;
  justify-content: space-between;
  gap: 22px;
  margin-block-end: 0;

  & > div {
    display: flex;
    flex-direction: column;
    width: 50%;
    color: $table-grey;
    gap: 8px;
    position: relative;
  }

  .select {
    height: 46px;
    border: 1px solid #e7e7e7;
    border-radius: 4px;
    padding: 0 5px;
    margin: 0;
    width: unset;
  }

  &.holiday {
    margin-block-end: 20px;
  }

  &.offday .input {
    width: 140px;
    padding: 0 14px;
    border: 1px solid $grey-border-color;
    border-radius: 4px;
    padding: 0 10px;
  }

  &.offday .select {
    width: 120px;
  }
}

.multi-select.offday {
  visibility: hidden;
  width: 0;
  height: 0;
  min-height: 0;
  padding: 0;
  margin: 0;
  position: fixed;
  transform: translate(-50%, -50%);

  * {
    visibility: none;
    width: 0;
    height: 0;
    min-height: 0;
    padding: 0;
    margin: 0;
  }
}

.holiday-search {
  margin-block-end: 10px;
  width: 100%;
}

.holiday-empty {
  padding: 24px 16px;
  text-align: center;
  color: $table-grey;
  font-size: 14px;
}

.holidays {
  display: flex;
  flex-direction: column;
  gap: 15px;
  border: 1px solid #e7e7e7;
  padding-block: 10px;
  max-height: 310px;
  min-height: 310px;
  min-width: 600px;
  overflow-y: scroll;
  @include scroll;

  .holiday {
    position: relative;
    display: flex;
    font-size: 14px;
    align-items: center;

    & > * {
      margin-inline-end: 15px;
    }

    & > label {
      margin-inline-start: 25px;
    }

    .name {
      width: 200px;
    }

    .month {
      width: 80px;
    }

    .day {
      width: 80px;
    }

    .year {
      width: 45px;
    }

    &:not(:last-of-type)::after {
      content: '';
      height: 1px;
      background-color: #e7e7e7;
      width: 93%;
      position: absolute;
      bottom: -7px;
      left: 17px;
      pointer-events: none;
    }

    &:hover {
      .action,
      .cell-button {
        visibility: unset;
      }
    }

    &.is-editing {
      background-color: #e8f4fd;
      border-radius: 4px;
    }
  }
}

.titles {
  display: flex;
  font-size: 16px;
  margin-block-end: 6px;

  & > * {
    margin-inline-end: 15px;
  }

  & > div {
    width: 80px;
  }

  & > div:first-of-type {
    margin-inline-start: 25px;
    width: 230px;
  }

  & > div:last-of-type {
    width: 45px;
  }
}

.cell-icon {
  &:hover {
    cursor: pointer;
  }

  &.action {
    visibility: hidden;
  }
}

.selected-days-table {
  border: 1px solid #e7e7e7;
  overflow-y: auto;
  overflow-x: hidden;
  flex-grow: 1;
  max-height: 320px;
  @include scroll;

  .shift-holiday & {
    max-height: 660px;
  }
}

/* ─── Delete confirmation ─── */
.delete-confirmation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  border-radius: 10px;
  animation: fadeIn 0.15s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.delete-confirmation-dialog {
  background-color: $white;
  padding: 0;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  min-width: 380px;
  max-width: 460px;
  overflow: hidden;
  animation: slideUp 0.2s ease-out;
  display: flex;
  flex-direction: column;

  .confirmation-icon-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 28px 28px 12px;
    background: linear-gradient(180deg, #fff5f5 0%, #ffffff 100%);

    .confirmation-icon {
      width: 56px;
      height: 56px;
    }
  }

  .confirmation-content {
    padding: 16px 28px;
    text-align: center;

    .confirmation-title {
      font-size: 20px;
      font-weight: 600;
      color: #1a1a1a;
      margin-block-end: 8px;
    }

    .confirmation-message {
      font-size: 14px;
      color: #666;
      line-height: 1.5;
    }
  }

  .confirmation-buttons {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding: 16px 28px 24px;
    border-top: 1px solid #f0f0f0;
    background-color: #fafafa;

    .cancel-btn {
      min-width: 90px;
      font-weight: 500;
    }

    .confirm-btn {
      min-width: 90px;
      font-weight: 500;
      background-color: #dc3545 !important;
      color: white !important;

      &:hover {
        background-color: #c82333 !important;
      }
    }
  }
}

@keyframes slideUp {
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
</style>
